import React, { useEffect, useRef, useCallback } from "react";
import { Text, theme } from "@ifgengineering/component-library";
import styled, { keyframes } from "styled-components";
import { StartupsListContainer } from "./styled";
import { EisFundStartup } from "./types";
import EisFundStartupCard from "./EisFundStartupCard";

import magnifyingGlassImage from "@assets/images/magnifying-glass.png";

// Skeleton animation and components for loading state
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Skeleton = styled.div<{
  width?: string;
  height?: string;
  radius?: string;
}>`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "20px"};
  background: linear-gradient(
    90deg,
    ${theme.colors.SLATE200} 25%,
    ${theme.colors.SLATE300} 50%,
    ${theme.colors.SLATE200} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${(props) => props.radius || "8px"};
`;

const SkeletonCircle = styled(Skeleton)`
  border-radius: 50%;
`;

const StartupCardSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.WHITE};
  border-radius: 16px;
  padding: 20px 24px;
  box-shadow: 0 1px 6px rgba(52, 111, 180, 0.1);
`;

const SkeletonCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const SkeletonCardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: ${theme.colors.SLATE100};
  padding: 8px 12px;
  border-radius: 8px;
`;

const SkeletonCardDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const SkeletonDetailGroup = styled.div`
  display: flex;
  gap: 32px;
`;

const SkeletonDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StartupCardSkeletonItem: React.FC = () => (
  <StartupCardSkeleton>
    <SkeletonCardHeader>
      <SkeletonCardHeaderLeft>
        <SkeletonCircle width="40px" height="40px" />
        <Skeleton width="120px" height="16px" />
      </SkeletonCardHeaderLeft>
      <Skeleton width="130px" height="16px" />
    </SkeletonCardHeader>
    <SkeletonCardDetails>
      <SkeletonDetailGroup>
        <SkeletonDetailItem>
          <Skeleton width="100px" height="14px" />
          <Skeleton width="80px" height="20px" />
        </SkeletonDetailItem>
        <SkeletonDetailItem>
          <Skeleton width="80px" height="14px" />
          <Skeleton width="60px" height="20px" />
        </SkeletonDetailItem>
      </SkeletonDetailGroup>
      <Skeleton width="140px" height="32px" radius="6px" />
    </SkeletonCardDetails>
  </StartupCardSkeleton>
);

interface EisFundStartupsListProps {
  startups: EisFundStartup[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** Current search query - used to determine empty state message */
  searchQuery?: string;
  /** Whether a search is in progress (debouncing or fetching) */
  isSearching?: boolean;
  /** Callback when a startup card is clicked */
  onStartupClick?: (startup: EisFundStartup) => void;
}

const EisFundStartupsList: React.FC<EisFundStartupsListProps> = ({
  startups,
  loading = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  searchQuery,
  isSearching = false,
  onStartupClick,
}) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Setup intersection observer for infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && onLoadMore) {
        onLoadMore();
      }
    },
    [hasMore, loadingMore, onLoadMore]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    observerRef.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    });

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  // Show skeleton cards for initial load only (no existing data)
  if (loading && startups.length === 0) {
    return (
      <StartupsListContainer data-testid="eis-startups-skeleton">
        {Array.from({ length: 5 }).map((_, index) => (
          <StartupCardSkeletonItem key={index} />
        ))}
      </StartupsListContainer>
    );
  }

  // Show empty state (only when not loading/searching and no results)
  // When searching, keep showing existing results until new ones arrive
  if (!loading && !isSearching && startups.length === 0) {
    const hasSearchQuery = searchQuery && searchQuery.trim().length > 0;

    return (
      <EmptyStateContainer data-testid="eis-empty-state">
        <MagnifyingGlassImage src={magnifyingGlassImage} alt="" />
        <EmptyStateText type="T18" color="SLATE800" fontFamily="archiasemibold">
          {hasSearchQuery
            ? `We couldn't find a match for "${searchQuery}"`
            : "Your money hasn't been deployed into any startups yet."}
        </EmptyStateText>
      </EmptyStateContainer>
    );
  }

  return (
    <StartupsListContainer data-testid="eis-startups-list">
      {startups.map((startup) => (
        <EisFundStartupCard
          key={startup.id}
          startup={startup}
          onClick={onStartupClick}
        />
      ))}

      {/* Infinite scroll trigger element */}
      <LoadMoreTrigger ref={loadMoreRef}>
        {loadingMore && (
          <LoadingMoreContainer>
            <InlineSpinner />
            <Text type="P14" color="SLATE600">
              Loading more...
            </Text>
          </LoadingMoreContainer>
        )}
        {!hasMore && startups.length > 0 && (
          <EndOfListText>
            <Text type="P14" color="SLATE500">
              You've reached the end
            </Text>
          </EndOfListText>
        )}
      </LoadMoreTrigger>
    </StartupsListContainer>
  );
};

export default EisFundStartupsList;

const LoadMoreTrigger = styled.div`
  min-height: 20px;
  padding: 16px 0;
`;

const LoadingMoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
`;

const EndOfListText = styled.div`
  text-align: center;
  padding: 16px;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const InlineSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid ${theme.colors.SLATE200};
  border-top-color: ${theme.colors.BLUE600};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  background-color: ${theme.colors.SLATE100};
  border-radius: 16px;
`;

const MagnifyingGlassImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  margin-bottom: 24px;
`;

const EmptyStateText = styled(Text)`
  text-align: center;
  max-width: 300px;
`;
